import type { GlucoseEvent } from "@tandemdiabetes/sugarmate-api-react-client"
import { ActivityType } from "@tandemdiabetes/sugarmate-api-react-client"
import dayjs from "dayjs"
import {
  useMemo,
  useRef,
} from "react"

import { GraphActivities } from "./GraphActivities"
import type { Props as GraphActivitiesProps } from "./GraphActivities"
import { GraphGlucoseEntries } from "./GraphGlucoseEntries"
import { GraphGlucoseEvents } from "./GraphGlucoseEvents"
import styles from "./GraphItem.module.scss"
import { NowLine } from "./NowLine"
import { TimeDividers } from "./TimeDividers"
import { useItemStartAndStopTime } from "./useItemStartAndStopTime"
import { useSuppressedTimeDivider } from "./useSuppressedTimeDivider"
import { useTimeDividers } from "./useTimeDividers"
import { useFollowedUsers } from "src/contexts"
import { useFindUserEvents } from "src/hooks"
import {
  createEventQueryObjectForType,
  createStartedAtSelector,
} from "src/utils"

interface Props {
  index: number;
}

export function GraphItem(props: Props) {
  const { index } = props

  const { currentUserId } = useFollowedUsers()

  const [
    start,
    end,
  ] = useItemStartAndStopTime(index)

  const eventsSelector = useMemo(
    () => createEventQueryObjectForType(
      currentUserId,
      ActivityType.Glucose,
      {
        selector: createStartedAtSelector(
          start,
          end,
        ),
        sort: [{ started_at: "asc" as const }],
      },
    ),
    [
      start,
      end,
      currentUserId,
    ],
  )

  const {
    result: glucoseEvents,
    isFetching: isFetchingEvents,
  } = useFindUserEvents(eventsSelector)

  const timeDividers = useTimeDividers(start)

  // The divider label may be hidden because it would overlap the now line's
  // time label. Only relevant for the item that contains the now line (index 0).
  const suppressedTimeDivider = useSuppressedTimeDivider(
    start,
    timeDividers,
  )

  const lastReturnedGraphActivitiesPropertiesArrayRef = useRef<GraphActivitiesProps[]>([])

  // note: this only works if the time dividers align with the start and end of each graph item
  const graphActivitiesPropertiesArray = useMemo(
    (): GraphActivitiesProps[] => {
      let currentDividerIndex = 0

      // this gets the min, max, start, and end for each time divider
      const propsArray: GraphActivitiesProps[] = timeDividers.map(
        (time, dividerIndex) => ({
          minMgdl: undefined,
          maxMgdl: undefined,
          start: time,
          end: timeDividers[dividerIndex + 1] || end,
        }),
      )

      if (isFetchingEvents) {
        return lastReturnedGraphActivitiesPropertiesArrayRef.current
      }

      // this steps through the glucose events and calculates the min and max for each group
      (glucoseEvents as GlucoseEvent[]).forEach((glucoseEvent) => {
        const nextDivider = timeDividers[currentDividerIndex + 1]
        if (nextDivider && dayjs(glucoseEvent.started_at).isAfter(nextDivider)) {
          currentDividerIndex += 1
        }

        const currentProps = propsArray[currentDividerIndex]
        const reading = glucoseEvent.glucose.mg_dl

        if (currentProps === undefined) {
          // Sanity check - this should never happen, but we've seen it with test data that has readings from the future.
          console.log("GraphItem: currentProps is undefined (possibly due to readings from the future)")
          return
        }

        if (
          currentProps.minMgdl === undefined ||
          reading < currentProps.minMgdl
        ) {
          currentProps.minMgdl = reading
        }

        if (
          currentProps.maxMgdl === undefined ||
          reading > currentProps.maxMgdl
        ) {
          currentProps.maxMgdl = reading
        }
      })

      lastReturnedGraphActivitiesPropertiesArrayRef.current = propsArray
      return propsArray
    },
    [
      glucoseEvents,
      isFetchingEvents,
      timeDividers,
      end,
      lastReturnedGraphActivitiesPropertiesArrayRef,
    ],
  )

  return (
    <>
      {index === 0 && <NowLine />}
      <TimeDividers
        start={start}
        timeDividers={timeDividers}
        suppressLabelForTime={index === 0 ? suppressedTimeDivider : undefined}
      />
      <div className={styles.activitiesContainer}>
        {graphActivitiesPropertiesArray.map((graphActivitiesProperties) => (
          <GraphActivities
            key={graphActivitiesProperties.start}
            {...graphActivitiesProperties}
          />
        ))}
      </div>
      <GraphGlucoseEvents
        start={start}
        events={glucoseEvents as GlucoseEvent[]}
      />
      <GraphGlucoseEntries
        start={start}
        end={end}
      />
    </>
  )
}
